import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import EmailInput from '@/components/form/EmailInput.tsx';
import useInitiateOnlinePayment from '@/features/dashboard/onlinePayments/hooks/useInitiateOnlinePayment.ts';
import {
  initiatePaymentSchema,
  InitiatePaymentFormData,
  InitiatePaymentInputData,
  initiatePaymentDefaultValues,
} from '@/features/dashboard/onlinePayments/schemas/initiatePaymentSchema.ts';

interface InitiatePaymentModalProps {
  plannedTransactionId: string;
  onClose: () => void;
}

const InitiatePaymentModal = ({
  plannedTransactionId,
  onClose,
}: InitiatePaymentModalProps) => {
  const initiateMutation = useInitiateOnlinePayment();

  const methods = useForm<InitiatePaymentInputData>({
    resolver: zodResolver(initiatePaymentSchema),
    defaultValues: {
      ...initiatePaymentDefaultValues,
      successUrl: `${window.location.origin}/payment/success`,
      errorUrl: `${window.location.origin}/payment/error`,
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const onSubmit = (data: InitiatePaymentFormData) => {
    initiateMutation.mutate(
      {
        plannedTransactionId,
        provider: 'TPAY',
        payerEmail: data.payerEmail || undefined,
        payerFirstName: data.payerFirstName || undefined,
        payerLastName: data.payerLastName || undefined,
        successUrl: data.successUrl,
        errorUrl: data.errorUrl,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Zainicjuj płatność online" onClose={onClose} />
          <ModalContent>
            <EmailInput
              label="E-mail płatnika"
              register={register('payerEmail')}
              error={errors.payerEmail?.message}
            />
            <TextInput
              label="Imię płatnika"
              register={register('payerFirstName')}
              error={errors.payerFirstName?.message}
              placeholder="Opcjonalnie"
            />
            <TextInput
              label="Nazwisko płatnika"
              register={register('payerLastName')}
              error={errors.payerLastName?.message}
              placeholder="Opcjonalnie"
            />
            <TextInput
              label="URL sukcesu"
              required
              register={register('successUrl')}
              error={errors.successUrl?.message}
            />
            <TextInput
              label="URL błędu"
              required
              register={register('errorUrl')}
              error={errors.errorUrl?.message}
            />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={initiateMutation.isPending}
            >
              Zainicjuj płatność
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default InitiatePaymentModal;
